import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Moon } from "@phosphor-icons/react";
import { TopBar } from "../components/TopBar";
import { AuthLock } from "../components/AuthLock";
import { useAuth } from "../lib/auth";
import { createRecce, listRecces, getMyCredits, getDigest } from "../lib/api";

const CYCLE_WORDS = ["sleep", "are on a run", "are on a date", "watch a movie"];

const EXAMPLE_CHIPS = [
  {
    label: "Sneaker launch film",
    idea: "A sneaker launch film shot like a 90s skate video, grainy and handheld",
  },
  {
    label: "Warm fintech brand",
    idea: "A fintech brand that feels warm instead of corporate — like a well-worn leather wallet",
  },
  {
    label: "2am diner doc",
    idea: "A short doc about small-town diners at 2am",
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [idea, setIdea] = useState("");
  const [aiOn, setAiOn] = useState(false);
  const [sending, setSending] = useState(false);
  const [recces, setRecces] = useState([]);
  const [credits, setCredits] = useState(null);
  const [digest, setDigest] = useState(null);

  // Cycling hero word
  const cycleRef = useRef(null);
  const [cycleIdx, setCycleIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setCycleIdx((i) => (i + 1) % CYCLE_WORDS.length);
      if (cycleRef.current) {
        cycleRef.current.classList.remove("animate-cycle-swap");
        // force reflow to restart animation
        // eslint-disable-next-line no-unused-expressions
        void cycleRef.current.offsetWidth;
        cycleRef.current.classList.add("animate-cycle-swap");
      }
    }, 2800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Only fetch user-scoped data once auth is established.
    if (!user) return;
    listRecces().then(setRecces).catch(() => {});
    getMyCredits().then(setCredits).catch(() => {});
    getDigest().then(setDigest).catch(() => {});
  }, [user]);

  const submit = async () => {
    const trimmed = idea.trim();
    if (!trimmed) return;
    // Unauth users: pop the auth overlay instead of hitting the backend.
    if (!user) return;
    setSending(true);
    try {
      const title = trimmed.length > 70 ? trimmed.slice(0, 70) + "…" : trimmed;
      const r = await createRecce({
        title,
        brief: trimmed,
        medium: "general",
        ai_generated: aiOn,
      });
      toast("Recce dispatched", {
        description: "Scouting the open web for references…",
      });
      navigate(`/recce/${r.id}`);
    } catch (e) {
      toast.error("Couldn't dispatch Recce", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
      setSending(false);
    }
  };

  const showLock = !authLoading && !user;

  return (
    <div className="min-h-screen bg-bg">
      <TopBar />

      <main
        className="max-w-[920px] mx-auto px-6 pb-20"
        data-testid="intake-page"
      >
        {/* INTAKE */}
        <section className="pt-14 text-center" id="intake">
          <p
            className="text-[12px] font-semibold tracking-[1.5px] uppercase text-amber mb-4"
            data-testid="intake-eyebrow"
          >
            Reference scouting, always on
          </p>
          <h1
            className="font-serif font-medium leading-[1.2] mx-auto mb-5 whitespace-normal sm:whitespace-nowrap"
            style={{ fontSize: "clamp(25px, 3.5vw, 38px)" }}
          >
            Digs for references while you{" "}
            <span className="inline-block relative align-top text-left">
              <span
                ref={cycleRef}
                className="inline-block text-amber relative whitespace-nowrap animate-cycle-swap"
                data-testid="cycle-word"
              >
                {CYCLE_WORDS[cycleIdx]}
              </span>
            </span>
          </h1>
          <p className="text-ink-dim text-[15.5px] leading-[1.6] max-w-[52ch] mx-auto mb-2.5">
            You share a raw idea. Recce explores the open web and returns a moodboard of
            references you'd have spent a week digging up alone.
          </p>
          <p className="text-ink-dim text-[14.5px] opacity-80 max-w-[52ch] mx-auto mb-10">
            It doesn't do the creative thinking — it gives you back the time to do it yourself.
          </p>

          <div className="relative max-w-[640px] mx-auto">
            <div
              className="bg-surface border border-line rounded-2xl p-5 text-left"
              style={{ boxShadow: "0 1px 2px rgba(28,26,22,0.03)" }}
              data-testid="idea-box"
            >
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="e.g. A perfume ad that feels like walking through a foggy orchard at 5am"
              className="w-full min-h-[88px] resize-y bg-transparent border-none outline-none text-ink text-[17px] leading-[1.5] placeholder:text-[#a79f8e]"
              data-testid="idea-input"
              disabled={sending}
            />
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-line gap-3 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[12.5px] text-ink-ghost">
                  10 references per board · free tier scouts on demand
                </span>
                <label
                  className="inline-flex items-center gap-2 text-[12.5px] text-ink-dim cursor-pointer select-none"
                  data-testid="ai-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={aiOn}
                    onChange={(e) => setAiOn(e.target.checked)}
                    className="w-3.5 h-3.5 accent-amber"
                    data-testid="ai-toggle"
                  />
                  Add AI-generated images
                </label>
              </div>
              <button
                type="button"
                onClick={submit}
                disabled={sending || !idea.trim()}
                data-testid="send-btn"
                className="bg-amber text-[#20170a] rounded-full px-5 py-3 text-[14.5px] font-semibold cursor-pointer transition-[transform,background] duration-150 hover:bg-amber-hover active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? "Sending…" : "Send Recce out →"}
              </button>
            </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {EXAMPLE_CHIPS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setIdea(c.idea)}
                  className="text-[12.5px] text-ink-dim bg-surface border border-line rounded-full px-3.5 py-[7px] hover:border-amber-dim hover:text-ink transition-colors cursor-pointer"
                  data-testid={`chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {showLock && <AuthLock />}
          </div>
        </section>

        {/* OVERNIGHT DIGEST — what Recce brought you since you last looked */}
        {digest && digest.total_new > 0 && (
          <section className="mt-16" data-testid="overnight-digest">
            <div className="flex items-baseline justify-between border-b border-line pb-3 mb-5">
              <h2 className="font-serif text-[20px] font-medium flex items-center gap-2">
                <Moon size={16} weight="fill" className="text-amber" />
                What Recce brought you overnight
              </h2>
              <span className="text-[12px] text-ink-dim" data-testid="digest-total">
                {digest.total_new} new reference{digest.total_new === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {digest.items.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/recce/${it.id}`)}
                    data-testid={`digest-item-${it.id}`}
                    className="w-full text-left bg-surface border border-line rounded-xl p-4 flex items-center gap-4 hover:border-amber-dim transition-colors"
                  >
                    <DigestPreview preview={it.preview} />
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-[16px] leading-tight truncate">
                        {it.title}
                      </div>
                      <div className="text-[11.5px] text-ink-dim mt-1">
                        <span className="text-amber font-medium">
                          +{it.count} new
                        </span>
                        {it.latest_at && (
                          <span className="text-ink-ghost">
                            {" · last added "}
                            {fmtAgo(it.latest_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* PAST BOARDS (if any) */}
        {recces.length > 0 && (
          <section className="mt-20" data-testid="past-boards">
            <div className="flex items-baseline justify-between border-b border-line pb-3 mb-6">
              <h2 className="font-serif text-[22px] font-medium">Your boards</h2>
              <span className="text-[12px] text-ink-dim">
                {recces.length} active {recces.length === 1 ? "idea" : "ideas"}
              </span>
            </div>
            <ul className="divide-y divide-line border-y border-line">
              {recces.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/recce/${r.id}`)}
                    data-testid={`board-row-${r.id}`}
                    className="w-full text-left flex items-start gap-4 py-4 group"
                  >
                    <StatusDot status={r.status} />
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg leading-tight group-hover:text-amber transition-colors truncate">
                        {r.title || "Untitled"}
                      </div>
                      <div className="text-[11.5px] text-ink-ghost mt-1 truncate">
                        {r.status} · {fmtAgo(r.updated_at)}
                        {r.watching && " · ◉ watching"}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

const StatusDot = ({ status }) => {
  const color =
    status === "ready"
      ? "bg-emerald-500"
      : status === "scouting"
      ? "bg-amber animate-pulse"
      : status === "error"
      ? "bg-red-400"
      : "bg-line";
  return <span className={`mt-2.5 h-[6px] w-[6px] rounded-full shrink-0 ${color}`} />;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigestPreview = ({ preview }) => {
  const items = (preview || []).slice(0, 3);
  if (items.length === 0) {
    return (
      <div className="w-16 h-16 rounded-lg bg-surface-hi border border-line shrink-0" />
    );
  }
  return (
    <div className="flex -space-x-3 shrink-0" aria-hidden="true">
      {items.map((p, i) => {
        const src = p.asset_id ? `${API}/assets/${p.asset_id}` : p.image_url;
        return (
          <img
            key={i}
            src={src}
            alt=""
            className="w-14 h-14 rounded-lg border-2 border-bg object-cover bg-surface-hi"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        );
      })}
    </div>
  );
};

function fmtAgo(iso) {
  if (!iso) return "just now";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
